import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { formatClockTime } from "@/lib/date";
import { Avatar, Spinner } from "@/components/peeps/ui";

const GREEN = "#3FBF7F";
const GRAY = "#8A8B96";

export function PresenceDot({ status, size = 12 }) {
  if (!status) return null; // absence of data = no dot
  const color = status === "free" ? GREEN : GRAY;
  return (
    <span
      data-testid={`presence-dot-${status}`}
      className="rounded-full inline-block"
      style={{ width: size, height: size, background: color, border: `2px solid ${colors.surface}` }}
    />
  );
}

export function MemberStatusList({ groupId, accent = colors.brand, onColor = "#fff" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [mine, setMine] = useState({ status: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const restOfTodayHours = () => {
    const now = new Date();
    const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
    return Math.max(1, Math.min(24, Math.ceil((midnight - now) / 3600000)));
  };

  const load = useCallback(async () => {
    try {
      const res = await api.memberStatus(groupId);
      setMembers(res.members || []);
      setMine(res.my_status || { status: null });
    } catch (e) {
      // silent — availability is a nice-to-have overlay
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const goFree = async (hours) => {
    setBusy(true);
    setPickerOpen(false);
    try {
      const r = await api.setFree(groupId, hours);
      setMine({ status: "free", until: r.until });
      toast(`You're marked free until ${formatClockTime(r.until)}`);
      await load();
    } catch (e) { toast(e.message || "Couldn't update"); }
    finally { setBusy(false); }
  };

  const clearFree = async () => {
    setBusy(true);
    try {
      await api.clearFree(groupId);
      setMine({ status: null });
      await load();
    } catch (e) { toast(e.message || "Couldn't update"); }
    finally { setBusy(false); }
  };

  const freeNow = members.filter((m) => m.status === "free").length;

  return (
    <div data-testid="member-status-list" className="rounded-2xl p-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Who's around</p>
          <p className="font-display text-[12px]" style={{ color: colors.faint }}>
            {freeNow > 0 ? `${freeNow} free right now` : "Tap “I’m free” so people know you’re around"}
          </p>
        </div>
        {mine.status === "free" ? (
          <button data-testid="clear-free-button" onClick={clearFree} disabled={busy}
            className="flex items-center gap-1.5 rounded-full py-2 px-3.5" style={{ background: GREEN + "22", border: `1px solid ${GREEN}` }}>
            <span className="rounded-full inline-block" style={{ width: 8, height: 8, background: GREEN }} />
            <span className="font-display font-medium text-[12.5px]" style={{ color: GREEN }}>
              Free till {mine.until ? formatClockTime(mine.until) : "later"} · clear
            </span>
          </button>
        ) : (
          <button data-testid="im-free-button" onClick={() => setPickerOpen((v) => !v)} disabled={busy}
            className="flex items-center gap-1.5 rounded-full py-2 px-4 transition-transform active:scale-95" style={{ background: accent }}>
            {busy ? <Spinner style={{ borderTopColor: onColor }} /> : <span className="rounded-full inline-block" style={{ width: 8, height: 8, background: onColor }} />}
            <span className="font-display font-bold text-[12.5px]" style={{ color: onColor }}>I'm free</span>
          </button>
        )}
      </div>

      {pickerOpen && mine.status !== "free" && (
        <div data-testid="free-duration-picker" className="flex gap-2 mb-3">
          {[{ l: "1 hour", h: 1 }, { l: "3 hours", h: 3 }, { l: "Rest of today", h: restOfTodayHours() }].map((o) => (
            <button
              key={o.l}
              data-testid={`free-duration-${o.h === 1 ? "1h" : o.h === 3 ? "3h" : "today"}`}
              onClick={() => goFree(o.h)}
              disabled={busy}
              className="flex-1 rounded-xl py-2.5 transition-transform active:scale-95"
              style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}
            >
              <span className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurface }}>{o.l}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex py-3"><Spinner /></div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {members.map((m) => (
            <button key={m.user_id} data-testid={`member-status-${m.user_id}`} onClick={() => navigate(m.user_id === user?.id ? "/profile" : `/user/${m.user_id}`)} className="flex flex-col items-center gap-1 shrink-0" style={{ width: 58 }}>
              <div className="relative">
                <Avatar name={m.member?.display_name} uri={m.member?.profile_photo_url} size={44} />
                {m.status && (
                  <span className="absolute bottom-0 right-0">
                    <PresenceDot status={m.status} size={13} />
                  </span>
                )}
              </div>
              <span className="font-display text-[11px] text-center truncate w-full" style={{ color: colors.onSurfaceTertiary }}>
                {m.user_id === user?.id ? "You" : (m.member?.display_name || "").split(" ")[0]}
              </span>
              {m.status && (
                <span className="font-display text-[9.5px] text-center truncate w-full" style={{ color: m.status === "free" ? GREEN : GRAY }}>
                  {m.status === "free" ? "Free" : (m.label || "Busy")}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
